import {
  DashboardChartCard,
  Badge,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  cn,
} from '@yeshuman/sdk'
import type { ChartConfig } from '@yeshuman/sdk'
import { Cell, Pie, PieChart } from 'recharts'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import type {
  CallOperatorCallMetrics,
  CallOperatorOutcomeMetrics,
} from '../lib/call-operator-metrics'

function formatDuration(seconds: number | null) {
  if (seconds == null) return '—'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins <= 0) return `${secs}s`
  return `${mins}m ${secs}s`
}

function truncateLabel(s: string, max = 28) {
  if (s.length <= max) return s
  return `${s.slice(0, max - 1)}…`
}

const evalDonutConfig = {
  completed: { label: 'Evaluated', color: 'var(--chart-1)' },
  remaining: { label: 'Remaining', color: 'var(--muted)' },
} satisfies ChartConfig

const endedConfig = {
  count: { label: 'Count', color: 'var(--chart-2)' },
} satisfies ChartConfig

export type CallAgentPerformancePanelProps = {
  callMetrics: CallOperatorCallMetrics
  outcomeMetrics: CallOperatorOutcomeMetrics
  loading?: boolean
  className?: string
}

export function CallAgentPerformancePanel({
  callMetrics,
  outcomeMetrics,
  loading = false,
  className,
}: CallAgentPerformancePanelProps) {
  const completed = callMetrics.evaluationsCompleteCount
  const remaining = Math.max(
    0,
    callMetrics.completedInWindow - callMetrics.evaluationsCompleteCount
  )
  const evalPct = callMetrics.evaluationsCompletePct
  const evalDonutData =
    callMetrics.completedInWindow > 0
      ? [
          { name: 'completed', value: completed, fill: 'var(--color-completed)' },
          { name: 'remaining', value: remaining, fill: 'var(--color-remaining)' },
        ]
      : [{ name: 'remaining', value: 1, fill: 'var(--color-remaining)' }]

  const booked = outcomeMetrics.bookingsMade
  const fallOff =
    outcomeMetrics.fallOffBookingRequested + outcomeMetrics.fallOffCancelled
  const openOnly = outcomeMetrics.completedCallsOutcomeOpenOnly
  const noOutcome = outcomeMetrics.completedCallsNoOutcome
  const segmentTotal = booked + fallOff + openOnly + noOutcome
  const segments = [
    {
      key: 'booked',
      label: 'Booked',
      count: booked,
      className: 'bg-success',
    },
    {
      key: 'falloff',
      label: 'Fall-off',
      count: fallOff,
      className: 'bg-warning',
    },
    {
      key: 'open',
      label: 'Open only',
      count: openOnly,
      className: 'bg-primary/60',
    },
    {
      key: 'none',
      label: 'No outcome',
      count: noOutcome,
      className: 'bg-muted-foreground/40',
    },
  ].filter((s) => s.count > 0)

  const endedData = callMetrics.endedReasonSeries.slice(0, 6).map((s) => ({
    name: truncateLabel(s.name),
    count: s.count,
  }))

  const { urgencyCounts } = callMetrics
  const urgencyTotal =
    urgencyCounts.emergency + urgencyCounts.elevated + urgencyCounts.routine

  return (
    <div className={cn('space-y-4', className)}>
      <div className="grid grid-cols-1 gap-4 @3col:grid-cols-3">
        <DashboardChartCard
          title="Evaluation completion"
          description={
            evalPct != null
              ? `${completed} of ${callMetrics.completedInWindow} completed calls`
              : 'No completed calls in window'
          }
          loading={loading}
        >
          <div className="relative">
            <ChartContainer
              config={evalDonutConfig}
              className="mx-auto aspect-square max-h-[180px] w-full"
            >
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={evalDonutData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="62%"
                  outerRadius="88%"
                  strokeWidth={2}
                >
                  {evalDonutData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold tabular-nums text-foreground">
                {loading ? '…' : evalPct != null ? `${evalPct}%` : '—'}
              </span>
              <span className="text-[10px] text-muted-foreground">evaluated</span>
            </div>
          </div>
        </DashboardChartCard>

        <DashboardChartCard
          title="Booking conversion"
          description={
            outcomeMetrics.bookingConversionPct != null
              ? `${outcomeMetrics.bookingConversionPct}% of completed calls booked`
              : 'No completed calls to convert'
          }
          loading={loading}
        >
          <div className="flex h-full min-h-[180px] flex-col justify-center gap-4 px-1">
            <div>
              <div className="text-3xl font-bold tabular-nums">
                {loading ? '…' : booked}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Bookings made
                {fallOff > 0
                  ? ` · ${fallOff} fall-off (${outcomeMetrics.fallOffBookingRequested} requested · ${outcomeMetrics.fallOffCancelled} cancelled)`
                  : null}
              </p>
            </div>
            {segmentTotal > 0 ? (
              <>
                <div
                  className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
                  role="img"
                  aria-label="Booking outcome mix"
                >
                  {segments.map((seg) => (
                    <div
                      key={seg.key}
                      className={cn('h-full', seg.className)}
                      style={{ width: `${(seg.count / segmentTotal) * 100}%` }}
                      title={`${seg.label}: ${seg.count}`}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  {segments.map((seg) => (
                    <span key={seg.key} className="inline-flex items-center gap-1.5">
                      <span
                        className={cn('size-2 rounded-full', seg.className)}
                        aria-hidden
                      />
                      {seg.label} ({seg.count})
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No outcome data yet.</p>
            )}
          </div>
        </DashboardChartCard>

        <DashboardChartCard
          title="Urgency mix"
          description="AI-assessed call state across the window"
          loading={loading}
        >
          <div className="flex h-full min-h-[180px] flex-col justify-center gap-3 px-1">
            {(
              [
                {
                  key: 'emergency',
                  label: 'Emergency',
                  count: urgencyCounts.emergency,
                  badge: 'destructive' as const,
                },
                {
                  key: 'elevated',
                  label: 'Elevated',
                  count: urgencyCounts.elevated,
                  badge: 'warningSoft' as const,
                },
                {
                  key: 'routine',
                  label: 'Routine',
                  count: urgencyCounts.routine,
                  badge: 'secondary' as const,
                },
              ] as const
            ).map((row) => {
              const pct =
                urgencyTotal > 0
                  ? Math.round((100 * row.count) / urgencyTotal)
                  : 0
              return (
                <div key={row.key} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant={row.badge} className="font-normal">
                      {row.label}
                    </Badge>
                    <span className="text-sm font-medium tabular-nums">
                      {loading ? '…' : row.count}
                      <span className="text-muted-foreground font-normal ml-1">
                        ({pct}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-[width]',
                        row.key === 'emergency' && 'bg-destructive',
                        row.key === 'elevated' && 'bg-warning',
                        row.key === 'routine' && 'bg-primary/50'
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </DashboardChartCard>
      </div>

      <DashboardChartCard
        title="Call endings"
        description="Top end reasons in the current window"
        loading={loading}
      >
        {endedData.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No ended-reason data yet.
          </p>
        ) : (
          <ChartContainer config={endedConfig} className="min-h-[200px] w-full">
            <BarChart
              accessibilityLayer
              data={endedData}
              layout="vertical"
              margin={{ left: 8, right: 12, top: 8, bottom: 8 }}
            >
              <CartesianGrid horizontal={false} />
              <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
              <YAxis
                dataKey="name"
                type="category"
                width={120}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="count" fill="var(--color-count)" radius={4} />
            </BarChart>
          </ChartContainer>
        )}
      </DashboardChartCard>

      <p className="text-xs text-muted-foreground">
        Avg duration {formatDuration(callMetrics.avgDurationSeconds)}
        {callMetrics.medianDurationSeconds != null
          ? ` · median ${formatDuration(callMetrics.medianDurationSeconds)}`
          : null}
        {callMetrics.evaluationFailedCount > 0
          ? ` · ${callMetrics.evaluationFailedCount} evaluation failure${callMetrics.evaluationFailedCount === 1 ? '' : 's'}`
          : null}
      </p>
    </div>
  )
}
